import { toast } from '@gmg/gmg-react-components';
import { FC, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useEditOrganization } from 'src/graphql/useEditOrganization';
import { isGmgAdminRole } from 'src/role';
import { getAddOrganizationEvent, getEditOrganizationEvent, getOfflineLicensing } from 'src/tracking';
import AppContext from '../../AppContext';
import { useAddOrganization } from '../../graphql/useAddOrganization';
import { getErrorMsgForToast } from '../toastErrorHandler';
import AddOrEditOrgModal, { SubmitDataModel } from './AddOrEditOrgModal';
export interface NewOrgModalContainerProps {
    onClose?: () => void;
    existingOrgNames: string[] | undefined;
    toBeEditedOrganization?: SubmitDataModel;
};

const AddOrEditOrgModalContainer: FC<NewOrgModalContainerProps> = (props) => {
    const { t } = useTranslation();
    const addOrganization = useAddOrganization();
    const editOrganization = useEditOrganization(props.toBeEditedOrganization?.id!);
    const { trackEvent } = useContext(AppContext);
    const isEditCase = !!props.toBeEditedOrganization;

    const { user } = useContext(AppContext);
    const userRole = user?.role;
    const isGMGAdminOrSupportUser = isGmgAdminRole(userRole);

    const onSubmit = (formData: SubmitDataModel) => {
        const addOrEditOrgEvent = isEditCase ? getEditOrganizationEvent : getAddOrganizationEvent;
        trackEvent(addOrEditOrgEvent());

        if (formData.isAllowedToUseLicenseSystemsOffline) {
            trackEvent(getOfflineLicensing(props.toBeEditedOrganization?.id));
        }

        if (isEditCase) {
            const typedkeys = Object.keys(formData) as Array<keyof SubmitDataModel>;
            const hasPropBeenEdited = typedkeys.some(key => formData[key] !== props.toBeEditedOrganization![key]);
            if (!hasPropBeenEdited) {
                // if No changes have been detected just close the modal and do nothing (UX decision)
                props.onClose?.();
                return;
            }
        }

        const pendingPromise = isEditCase
            ? editOrganization.mutateAsync({
                ...formData,
                hasNameChanged: formData.name !== props.toBeEditedOrganization!.name,
            })
            : addOrganization.mutateAsync(formData);

        props.onClose?.();

        const successToastText = isEditCase
            ? t('AddOrEditOrgModalContainer.editorg_toast_succes_desc', {
                defaultValue: 'You have successfully edited "{{organizationName}}"!',
                organizationName: formData.name,
            })

            : t('AddOrganizationBtn.toast_success_desc', {
                defaultValue: 'You have successfully added "{{organizationName}}"!',
                organizationName: formData.name,
            });

        void toast.promise(pendingPromise, {
            loading: t('Common.Toast.Loading', 'Loading'),
            success: successToastText,
            error: response => getErrorMsgForToast(response, t),
        });
    };

    return (
        <AddOrEditOrgModal
            existingOrgNames={props.existingOrgNames}
            onSubmit={onSubmit}
            onClose={props.onClose}
            toBeEditedOrganization={props.toBeEditedOrganization}
            showIsAllowedToUseLicenseSystemsOfflineCheckBox={isGMGAdminOrSupportUser}
        />
    );
};

export default AddOrEditOrgModalContainer;
